import { useState, useRef, useEffect } from 'react'
import { motion } from 'framer-motion'
import { ShieldCheck, Loader2, ArrowLeft, Copy, Check } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { TOTPSetupResponse } from '@/lib/api/auth'
import toast from 'react-hot-toast'

export function TwoFactorSetupPage() {
  const { pendingTwoFactor, setup2FA, confirm2FA } = useAuth()
  const navigate = useNavigate()
  const [setupData, setSetupData] = useState<TOTPSetupResponse | null>(null)
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [isLoading, setIsLoading] = useState(false)
  const [isSettingUp, setIsSettingUp] = useState(true)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // Redirect if no pending 2FA setup
  useEffect(() => {
    if (!pendingTwoFactor || pendingTwoFactor.type !== '2fa_setup') {
      navigate('/login', { replace: true })
      return
    }
    initSetup()
  }, [pendingTwoFactor, navigate])

  const initSetup = async () => {
    try {
      const data = await setup2FA()
      setSetupData(data)
    } catch {
      toast.error('Failed to initialize 2FA setup')
      navigate('/login', { replace: true })
    } finally {
      setIsSettingUp(false)
    }
  }

  // Focus first input when setup data loads
  useEffect(() => {
    if (setupData) {
      inputRefs.current[0]?.focus()
    }
  }, [setupData])

  const handleCopySecret = async () => {
    if (!setupData) return
    try {
      await navigator.clipboard.writeText(setupData.secret)
      setCopied(true)
      toast.success('Secret copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Failed to copy')
    }
  }

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return

    const newCode = [...code]
    newCode[index] = value.slice(-1)
    setCode(newCode)
    setError('')

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }

    const fullCode = newCode.join('')
    if (fullCode.length === 6) {
      handleSubmit(fullCode)
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pasted.length === 6) {
      const newCode = pasted.split('')
      setCode(newCode)
      handleSubmit(pasted)
    }
  }

  const handleSubmit = async (fullCode?: string) => {
    const codeStr = fullCode || code.join('')
    if (codeStr.length !== 6) return

    setIsLoading(true)
    setError('')
    try {
      await confirm2FA(codeStr)
    } catch {
      setError('Invalid code. Make sure you scanned the QR code correctly.')
      setCode(['', '', '', '', '', ''])
      inputRefs.current[0]?.focus()
    } finally {
      setIsLoading(false)
    }
  }

  if (!pendingTwoFactor) return null

  if (isSettingUp) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-orange-50 dark:from-[#000742] dark:via-[#1a1f5c] dark:to-[#2a2f6c]">
        <div className="w-12 h-12 border-4 border-[#5869ff]/30 border-t-[#5869ff] rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden py-8">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-orange-50 dark:from-[#000742] dark:via-[#1a1f5c] dark:to-[#2a2f6c]" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md mx-4"
      >
        <div className="bg-white/90 dark:bg-[#000742]/90 backdrop-blur-xl rounded-2xl shadow-2xl p-8 border border-white/20 dark:border-[#5869ff]/20">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-4 bg-[#5869ff]/10 dark:bg-[#5869ff]/20 rounded-full flex items-center justify-center">
              <ShieldCheck className="w-8 h-8 text-[#5869ff]" />
            </div>
            <h1 className="text-2xl font-bold text-[#000742] dark:text-white">
              Set Up Two-Factor Authentication
            </h1>
            <p className="text-[#475467] dark:text-gray-400 mt-2 text-sm">
              Admin accounts require 2FA. Scan the QR code with your authenticator app.
            </p>
          </div>

          {/* QR Code */}
          {setupData && (
            <>
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-white rounded-xl shadow-sm">
                  <img
                    src={`data:image/png;base64,${setupData.qr_code}`}
                    alt="TOTP QR Code"
                    className="w-48 h-48"
                  />
                </div>
              </div>

              {/* Manual entry secret */}
              <div className="mb-6">
                <p className="text-xs text-center text-[#475467] dark:text-gray-400 mb-2">
                  Or enter this code manually:
                </p>
                <div className="flex items-center justify-center gap-2">
                  <code className="px-3 py-2 bg-gray-100 dark:bg-[#1a1f5c]/50 rounded-lg text-sm font-mono text-[#000742] dark:text-white tracking-wider">
                    {setupData.secret}
                  </code>
                  <button
                    onClick={handleCopySecret}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1a1f5c] text-gray-500 dark:text-gray-400 transition-colors"
                    title="Copy secret"
                  >
                    {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Supported apps note */}
              <div className="mb-6 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  <strong>Supported apps:</strong> Google Authenticator, Microsoft Authenticator, Authy, 1Password, or any TOTP-compatible app.
                </p>
              </div>
            </>
          )}

          {/* Verification code label */}
          <p className="text-sm font-medium text-[#344054] dark:text-gray-300 mb-3 text-center">
            Enter the 6-digit code from your app to verify:
          </p>

          {/* Code inputs */}
          <div className="flex justify-center gap-3 mb-4">
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => { inputRefs.current[index] = el }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                disabled={isLoading}
                className={`w-12 h-14 text-center text-xl font-bold rounded-lg border-2 transition-all
                  ${error
                    ? 'border-red-400 dark:border-red-500'
                    : 'border-gray-300 dark:border-[#5869ff]/30 focus:border-[#5869ff] dark:focus:border-[#5869ff]'
                  }
                  bg-white/50 dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white
                  focus:ring-2 focus:ring-[#5869ff]/20 focus:outline-none
                  disabled:opacity-50`}
              />
            ))}
          </div>

          {/* Error */}
          {error && (
            <p className="text-center text-sm text-red-500 dark:text-red-400 mb-4">
              {error}
            </p>
          )}

          {/* Submit */}
          <button
            onClick={() => handleSubmit()}
            disabled={isLoading || code.join('').length !== 6}
            className="w-full py-3 px-4 bg-gradient-to-r from-[#5869ff] to-[#000742] text-white rounded-lg font-medium hover:from-[#f8724f] hover:to-[#e85d3a] focus:outline-none focus:ring-2 focus:ring-[#5869ff] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Enabling 2FA...
              </>
            ) : (
              <>
                <ShieldCheck className="w-5 h-5" />
                Enable Two-Factor Authentication
              </>
            )}
          </button>

          {/* Back to login */}
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="w-full mt-4 py-2 text-sm text-[#475467] dark:text-gray-400 hover:text-[#5869ff] transition-colors flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </button>
        </div>
      </motion.div>
    </div>
  )
}
